<script lang="ts">
  import {onMount} from 'svelte'
  import {t} from '../i18n'
  import {isHandleValid, isUrlValid} from './utils'
  import Dropdown from '../shared/Dropdown.svelte'
  import type {Language, Subscription, UserExtended} from '../types'
  import ValidationMessage from '../shared/ValidationMessage.svelte'
  import {staticData} from '../stores/static'
  import api from '../api/api'
  import {user} from '../stores/user'
  import ChangePassword from './ChangePassword.svelte'
  import {flash} from '../stores/flash'
  import SpinnerSection from '../shared/SpinnerSection.svelte'
  import FacebookButton from '../../src/authentication/FacebookButton.svelte'
  import GoogleButton from '../../src/authentication/GoogleButton.svelte'

  let connectedProviders: string[] = []
  let subscriptions: Subscription[] = []
  let preferredLanguageId: number | undefined = undefined
  let userLanguages: Language[] = []
  let errors: Record<string, string> = {}
  let editedUser: UserExtended | undefined
  let loading = true

  onMount(() => {
    load()
  })

  async function load() {
    [editedUser, subscriptions, userLanguages, connectedProviders] = await Promise.all([
      api.get<UserExtended>(`/users/${$user?.id}`),
      api.get<Subscription[]>('/subscriptions'),
      api.get<Language[]>('/users/languages'),
      api.get<string[]>('/social_providers/connected')
    ])
    loading = false
  }

  const languages = staticData.languages
  const countries = staticData.countries
  const subscriptionTypes = staticData.subscriptionTypes

  $: if (preferredLanguageId) {
    const l = languages.find(l => l.id === preferredLanguageId)

    if (l && !userLanguages.includes(l)) {
      userLanguages = [...userLanguages, l]
    }

    preferredLanguageId = undefined
  }

  $: {
    errors['facebook_profile'] = !isUrlValid(editedUser?.facebook_profile) ? t.errors.validation.url : ''
    errors['vk_profile'] = !isUrlValid(editedUser?.vk_profile) ? t.errors.validation.url : ''
    errors['instagram_profile'] = !isUrlValid(editedUser?.instagram_profile) ? t.errors.validation.url : ''
    errors['website'] = !isUrlValid(editedUser?.website) ? t.errors.validation.url : ''
  }

  async function submit(e: Event) {
    if (!editedUser) return
    e.preventDefault()

    if (Object.values(errors).some(e => !!e)) return

    const payload = {
      user: {
        ...editedUser,
        subscriptions,
        languages: userLanguages
      }
    }

    $user = editedUser = await api.put<UserExtended>(`/users/${editedUser.id}`, payload)
    flash.show(t.flash.settings.success)
  }

  async function uploadPicture(event: Event) {
    if (!editedUser) return
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('user[avatar]', file)

    $user = editedUser = await api.put<UserExtended>(`/users/${editedUser.id}`, formData)
  }

  async function deleteImage() {
    if (!editedUser) return
    $user = editedUser = await api.put<UserExtended>(`/users/${editedUser.id}`, {user: {remove_avatar: true}})
  }

  function removeLanguage(language: Language) {
    userLanguages = userLanguages.filter((l) => l !== language)
  }

  function toggleSubscription(subscriptionType: any) {
    if (subscriptions.some((s) => s.subscription_type_id === subscriptionType.id)) {
      subscriptions = subscriptions.filter((s) => s.subscription_type_id !== subscriptionType.id)
    } else {
      subscriptions = [...subscriptions, {subscription_type_id: subscriptionType.id} as Subscription]
    }
  }

  function isSubscribed(subscriptionType: any) {
    return subscriptions.some((s) => s.subscription_type_id === subscriptionType.id)
  }

  function providerLogin(provider: string) {
    // TODO: trigger auth
    connectedProviders = [...connectedProviders, provider]
  }
</script>

{#if loading}
  <SpinnerSection/>
{:else}
  <div class="row">
    <div class="account_block">
      <h1>{t.user.settings.title}</h1>
      {#if editedUser}
        <form class="account__content" onsubmit={submit}>
          <div class="registration_info acc_section">
            <h4>{t.user.settings.registration_info}</h4>
            <div class="grid three">
              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.email}
                         type="email" required bind:value={editedUser.email}/>
                </div>
              </div>
              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.profile_url} type="text"
                         bind:value={editedUser.profile_handle}
                         class:error-input={!isHandleValid(editedUser.profile_handle)}/>
                </div>
              </div>
              <div class="grid__item">
                <div class="line">
                  <Dropdown bind:value={preferredLanguageId} icon={false}
                            options={languages.map(l => ({value: l.id, label: t.languages[l.code]}))}
                            class="ui inline search dropdown" autoclose={false}
                            emptyOption={t.languages.prompt} autocomplete>
                    <svelte:fragment slot="prefix">
                      <i class="dropdown icon icon-p-drop-arrow"/>

                      <span class="label-in-drop">
                        {t.user.settings.preferred_languages}
                      </span>
                    </svelte:fragment>
                  </Dropdown>
                </div>

                <div class="added__tags">
                  {#each userLanguages as l}
                    <div class="added__tag_item">
                      {t.languages[l.code]}
                      <i class="remove icon-p-close" onclick={() => removeLanguage(l)}/>
                    </div>
                  {/each}
                </div>
              </div>
            </div>
          </div>

          <div class="registration_info acc_section">
            <h4>{t.user.settings.user_info}</h4>
            <div class="grid three">
              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.first_name} bind:value={editedUser.first_name} required/>
                </div>
              </div>

              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.last_name} bind:value={editedUser.last_name} required/>
                </div>
              </div>

              <div class="grid__item">
                <div class="line">
                  <div class="user-avatar">
                    <img src={editedUser.avatar_big_thumgb} alt=""/>
                  </div>
                  <label for="image" class="change-avatar">{t.user.settings.change_avatar}</label>
                  <input id="image" type="file" onchange={uploadPicture} style="display: none;"/>

                  {#if editedUser.avatar_present}
                    <span class="delete-avatar" onclick={deleteImage}>
                      {t.user.settings.delete_avatar}
                    </span>
                  {/if}
                </div>
              </div>

              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.website} bind:value={editedUser.website}
                         class:error-input={errors.website}>
                  <ValidationMessage errors={[errors.website]}/>
                </div>
              </div>
            </div>
          </div>

          <div class="registration_info acc_section">
            <h4>{t.user.settings.social_info}</h4>
            <div class="grid three">
              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.facebook_profile} bind:value={editedUser.facebook_profile}
                         class:error-input={errors.facebook_profile}/>
                  <ValidationMessage errors={[errors.facebook_profile]}/>
                </div>
              </div>

              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.instagram_profile} bind:value={editedUser.instagram_profile}
                         class:error-input={errors.instagram_profile}/>
                  <ValidationMessage errors={[errors.instagram_profile]}/>
                </div>
              </div>

              <div class="grid__item">
                <div class="line">
                  <input placeholder={t.user.settings.vk_profile} bind:value={editedUser.vk_profile}
                         class:error-input={errors.vk_profile}/>
                  <ValidationMessage errors={[errors.vk_profile]}/>
                </div>
              </div>
            </div>
          </div>

          <div class="registration_info acc_section">
            <div class="grid three">
              <div class="grid__item grid__item--double">
                <div class="text_about rel">
                  <label for="aboutMe" class="db">
                    {t.user.settings.description}
                  </label>

                  <textarea id="aboutMe" class="bordered" bind:value={editedUser.description}></textarea>
                </div>
              </div>

              <div class="grid__item drop-list">
                <div class="line">
                  <input placeholder={t.user.settings.city} bind:value={editedUser.city}/>
                </div>

                <div class="line">
                  <Dropdown bind:value={editedUser.country_id} icon={false}
                            options={countries.map(l => ({value: l.id, label: t.countries[l.code]}))}
                            emptyOption={t.countries.not_selected} autocomplete>
                    <svelte:fragment slot="prefix">
                      <i class="dropdown icon icon-p-drop-arrow"/>

                      <span class="label-in-drop">
                        {t.user.settings.country}
                      </span>
                    </svelte:fragment>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>

          <div class="notification_settings acc_section">
            <h4>{t.user.settings.email_notifications}</h4>
            {#if subscriptions}
              <div class="grid three">
                {#each subscriptionTypes as st}
                  <div class="grid__item">
                    <div class="checkbox-wrap">
                      <input checked={isSubscribed(st)} type="checkbox" id={st.name_key}
                             onchange={() => toggleSubscription(st)}/>
                      <label for={st.name_key}>
                        <span>
                          <i class="icon-p-check"></i>
                        </span>
                        {t.user.settings.subscription_types[st.name_key]}
                      </label>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          </div>

          <div class="acc_section">
            <div class="grid four">
              <div class="grid__item">
                <button class="ui button black connected" onclick={submit}>{t.user.settings.save}</button>
              </div>
            </div>
          </div>

          <div class="social_connection acc_section">
            <h4>{t.user.settings.soc_connections}</h4>
            <div class="grid four">
              <div class="grid__item">
                <FacebookButton class="ui button black" disabled={connectedProviders.includes('facebook')}>
                  <i class="socicon-facebook"/>
                  {t.user.settings.soc_networks.facebook}
                </FacebookButton>
              </div>

              <div class="grid__item">
                <GoogleButton class="ui button black" disabled={connectedProviders.includes('google')}>
                  <i class="socicon-google"/>
                  {t.user.settings.soc_networks.google}
                </GoogleButton>
              </div>
            </div>
          </div>
        </form>
      {/if}
    </div>

    <div class="account_block">
      <div class="account__content">
        <div class="registration_info acc_section">
          <h4>{t.user.settings.change_password}</h4>
          <ChangePassword/>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>

  .icon-p-close, i.dropdown {
    cursor: pointer;
  }
</style>